import type { ApiResponse } from '@bower/shared';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

/** Wire-format AI quota state from GET /api/quota/me (string dates, not Date objects). */
export interface AiQuotaState {
  readonly used: number;
  readonly cap: number | null;
  readonly remaining: number | null;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly percentUsed: number;
  /**
   * Whether the cap would actually block. `false` = shadow mode: the counter
   * ticks but nothing refuses. Never warn or prompt an upgrade against an
   * unenforced cap.
   *
   * Optional on the wire so a frontend deployed ahead of the backend degrades
   * to "don't warn" rather than crashing on a missing field.
   */
  readonly enforced?: boolean;
}

/**
 * Typed error thrown by `unwrapApiResponse` when the http client resolved
 * a `{ success: false }` envelope. Carries the backend's structured error
 * code (e.g. `PRIVATE_MODE_DISABLED`, `QUOTA_EXCEEDED`) so callers can
 * branch on `error.code` instead of string-matching `error.message`.
 *
 * It carries the HTTP `status` too, because a code does not always decide the
 * remedy on its own. The connector-import read failures are one code
 * (`GRAPH_READ_FAILED`) whether the vendor answered 401, meaning the connected
 * account needs reauthorizing, or 5xx, meaning the vendor is down; the backend
 * folds that upstream status into 409 vs 503 and the status is the only thing
 * on the wire separating them. Anything narrower forces the next caller with
 * the same problem to fork its own error class.
 */
export class ApiResponseError extends Error {
  readonly code: string | undefined;
  readonly status: number | undefined;
  /** The envelope's `error.details`, e.g. bulk-move's per-item refusal reasons. */
  readonly details: Readonly<Record<string, unknown>> | undefined;
  constructor(
    message: string,
    code: string | undefined,
    status?: number,
    details?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = 'ApiResponseError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * Convert the http client's resolve-on-failure envelope into throw-on-failure.
 *
 * The client deliberately resolves `{ success: false }` instead of rejecting
 * (38+ hooks branch on `.success` without throwing — that contract must not
 * change). Hooks that DO want a rejection — every TanStack Query
 * `queryFn`/`mutationFn` — use this instead of hand-rolling
 * `if (!res.success) throw`.
 *
 * On success this returns `res.data` AS-IS, which is `undefined` for
 * endpoints that return no body — fine for mutations, but a TanStack
 * `queryFn` must not return undefined; validate `data` presence yourself
 * when the endpoint may omit it.
 */
export function unwrapApiResponse<T>(res: ApiResponse<T>, fallbackMessage: string): T {
  if (!res.success) {
    throw new ApiResponseError(
      // `||`, not `??`: an empty message is no message, and a toast built
      // from it would be blank.
      res.error?.message || fallbackMessage,
      res.error?.code,
      res.error?.status,
      res.error?.details,
    );
  }
  return res.data as T;
}

/**
 * Minimal HTTP primitives needed by domain API modules.
 * Implemented by ApiService; passed as the first argument to every
 * domain factory so the modules stay free of circular imports.
 */
export interface HttpClient {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>>;
  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>>;
  patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>>;
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  request<T = unknown>(config: AxiosRequestConfig, retries?: number): Promise<ApiResponse<T>>;
  getWorkspaceId(): string | null;
  getClient(): AxiosInstance;
}
